// The dials, as chip groups. Six settings decide how hard the run is; each one says in a sentence what
// it changes, because a dial whose effect you cannot predict is not a choice. No select elements
// anywhere — a chip is the only toggle in this interface (spec §8).

import type { Dials } from '../../sim/types';
import { Chip } from './ui';

export interface DialOption<T> { value: T; label: string; title: string }

export interface DialSpec<K extends keyof Dials> {
  key: K;
  /** The dial's name, acronyms already written out. */
  title: string;
  /** One sentence: what turning this dial changes about the fight. */
  blurb: string;
  options: DialOption<Dials[K]>[];
}

/** One spec per dial, each keeping its own value type. */
export type AnyDialSpec = { [K in keyof Dials]: DialSpec<K> }[keyof Dials];

export const DIALS: AnyDialSpec[] = [
  {
    key: 'c2',
    title: 'C2 (command and control)',
    blurb: 'How long an order takes to reach the unit after your headquarters transmits it.',
    options: [
      { value: 'off', label: 'off', title: 'Orders arrive the moment they are issued — the training-wheels setting' },
      { value: 'realistic', label: 'realistic', title: 'A minute or two of transmission delay, longer for units far from the headquarters' },
      { value: 'degraded', label: 'degraded', title: 'Twice the realistic delay: a net under strain' },
    ],
  },
  {
    key: 'ew',
    title: 'EW (electronic warfare)',
    blurb: 'How hard the enemy is jamming: it stretches order delays and shortens how long a drone survives overhead.',
    options: [
      { value: 'none', label: 'none', title: 'No jamming' },
      { value: 'moderate', label: 'moderate', title: 'Noticeable jamming; drones are hunted' },
      { value: 'heavy', label: 'heavy', title: 'Heavy jamming; orders crawl and drones do not last' },
    ],
  },
  {
    key: 'tod',
    title: 'Time of day',
    blurb: 'How far your sensors see, and how early you find the enemy.',
    options: [
      { value: 'dawn', label: 'dawn', title: 'Half light: sensors degraded, the classic attack hour' },
      { value: 'day', label: 'day', title: 'Full daylight: you see furthest — and so does the enemy' },
      { value: 'night', label: 'night', title: 'Night: short detection ranges, contact comes late and close' },
    ],
  },
  {
    key: 'red',
    title: 'Red posture',
    blurb: 'Where the enemy chooses to fight you; changing it re-lays the enemy across the ground.',
    options: [
      { value: 'screen', label: 'screen', title: 'Reconnaissance forward, main body held back to concentrate later' },
      { value: 'depth', label: 'depth', title: 'Defends in depth on the high ground, armored reserve behind' },
      { value: 'forward', label: 'forward', title: 'Massed well forward to stop the attack at PL RED' },
    ],
  },
  {
    key: 'pressure',
    title: 'Live pressure',
    blurb: 'With pressure on, the pause control is taken away: the clock runs whatever you are thinking about.',
    options: [
      { value: false, label: 'off', title: 'Pause is available during the fight' },
      { value: true, label: 'on', title: 'No pause: real time, all the way through' },
    ],
  },
  {
    key: 'planMin',
    title: 'Planning minutes',
    blurb: 'How many real minutes the planning clock gives you before the fight starts itself.',
    options: [
      { value: 3, label: '3 min', title: 'Three real minutes: a hasty attack' },
      { value: 5, label: '5 min', title: 'Five real minutes: the default' },
      { value: 10, label: '10 min', title: 'Ten real minutes: time to think it through' },
    ],
  },
];

type DialValue = Dials[keyof Dials];

export function DialGroup({
  spec, dials, onPick,
}: {
  spec: AnyDialSpec;
  dials: Dials;
  onPick: (patch: Partial<Dials>) => void;
}) {
  const value: DialValue = dials[spec.key];
  // The options keep their own value type per dial; widened once here so one component renders all six.
  const options = spec.options as DialOption<DialValue>[];
  return (
    <div data-testid={`dial-${spec.key}`} className="wt-card">
      <div className="mono text-[10px] uppercase tracking-[0.15em] text-paper">{spec.title}</div>
      <div className="mt-1 flex flex-wrap gap-1">
        {options.map((o) => (
          <Chip
            key={String(o.value)}
            active={o.value === value}
            title={o.title}
            testId={`dial-${spec.key}-${String(o.value)}`}
            onClick={() => onPick({ [spec.key]: o.value } as Partial<Dials>)}
          >
            {o.label}
          </Chip>
        ))}
      </div>
      <p className="mt-1 text-[10px] leading-snug text-fog">{spec.blurb}</p>
    </div>
  );
}
